import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import { useUserRole } from "@/services/userServices/useUserRole";
import { ADMIN } from "@/routes/Roles";
import React, { useEffect, useMemo, useCallback, useState } from "react";
import {
  findOverlayRow,
  getTaxonomyOverlays,
} from "@/services/taxonomyOverlayServices/taxonomyOverlayApi";

import {
  addSiteCategory,
  deleteSiteCategory,
  getCategories,
  getSiteCategories,
  getSiteCategoriesToAdd,
} from "@/services/categoryServices/categoryAction";
import SiteCategoriesView from "./SiteCategoriesView";
import NotificationAlertOptions from "@/layouts/Alerts";
import ConfirmDelete from "@/components/ConfirmDelete";
import { useNavigate } from "react-router-dom";
import { asCaught } from "@/services/caughtError";
import type { TaxonomyOverlayRow } from "@/services/taxonomyOverlayServices/taxonomyOverlayApi";

const NO_OVERLAYS: TaxonomyOverlayRow[] = [];

const SiteCategoriesContainer = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const categoriesSlice = useAppSelector((s) => s.categories || {}) as any;
  const permission = useCurrentPermission();
  const role = useUserRole();
  const currentSite = useAppSelector((s) => s.sites?.site || {});

  const siteCategories = categoriesSlice.siteCategories || [];
  const categoriesToAdd = categoriesSlice.categories_toadd || [];

  // Fetch data on mount
  useEffect(() => {
    dispatch(getSiteCategories());
    dispatch(getCategories());
    dispatch(getSiteCategoriesToAdd());
  }, [dispatch]);

  // SCRUM-1715 (SCRUM-1709 slice 2): the site's TaxonomyOverlay rows, read once
  // per site (the PageFormContainer shape). The Label column shows the row the
  // engine and the public site resolve on, and nothing else (SCRUM-1851): a
  // category this site has not labelled through the overlay shows an empty
  // label, not its stored `label[]` entry (gone from the schema, SCRUM-1716).
  const [siteOverlays, setSiteOverlays] = useState(NO_OVERLAYS);

  useEffect(() => {
    const siteId = currentSite?._id;
    if (!siteId) return undefined;
    let cancelled = false;
    getTaxonomyOverlays(siteId)
      .then((rows) => {
        if (!cancelled) setSiteOverlays(rows);
      })
      .catch(() => {
        // No rows is the fallback branch, not an error the table can act on.
        if (!cancelled) setSiteOverlays(NO_OVERLAYS);
      });
    return () => {
      cancelled = true;
    };
  }, [currentSite?._id]);

  // Reformat labels so the table shows the correct label per current site
  const reformattedCategories = useMemo(() => {
    return (siteCategories || []).map((prop) => {
      // the overlay row for this (site, category) wins outright — same shape
      // the view reads (`label[0].value.label`), so the column needs no change
      const row = findOverlayRow(siteOverlays, "category", prop._id);
      if (row) {
        return {
          ...prop,
          label: [{ siteid: currentSite?._id, value: { label: row.label, slug: row.slug } }],
        };
      }
      // no row: the empty label the view renders for an unlabelled category
      return { ...prop, label: [{ value: { label: "" } }] };
    });
  }, [siteCategories, siteOverlays, currentSite?._id]);

  // Handlers
  const handleAdd = useCallback(
    async (idCategory) => {
      // Only confirm when the assignment succeeds; addSiteCategory now re-throws on failure
      // (SCRUM-815).
      try {
        await dispatch(addSiteCategory(idCategory));
        NotificationAlertOptions("success", "Category assigned to site.");
      } catch (err) {
        NotificationAlertOptions(
          "error",
          asCaught(err)?.response?.data?.message || "Assigning the category failed. Please try again."
        );
      }
    },
    [dispatch]
  );

  // SCRUM-2022: "Remove" asks first, like every other destructive list action
  // (ConfirmDelete, SCRUM-1356). Until then the first click sent the PUT.
  const [alert, setAlert] = useState<React.ReactNode>(null);
  const hideAlert = useCallback(() => setAlert(null), []);

  const handleDelete = useCallback(
    (idCategory) => {
      // ConfirmDelete toasts success only after the removal resolves;
      // deleteSiteCategory re-throws on failure (SCRUM-815).
      setAlert(
        <ConfirmDelete
          title="Would you like to remove this category from the site?"
          onClose={hideAlert}
          onConfirm={() => dispatch(deleteSiteCategory(idCategory))}
          onSuccess={() => NotificationAlertOptions("success", "Category removed from site.")}
          onError={(err) =>
            NotificationAlertOptions(
              "error",
              asCaught(err)?.response?.data?.message || "Removing the category failed. Please try again."
            )
          }
          showCancel
        />
      );
    },
    [dispatch, hideAlert]
  );

  const handleLabel = useCallback(
    (idCategory) => {
      navigate(`/admin/category/label/${idCategory}`);
    },
    [navigate]
  );

  const handleScore = useCallback(
    (idCategory) => {
      navigate(`/admin/category/score/${idCategory}`);
    },
    [navigate]
  );

  return (
    <>
      {alert}
      <SiteCategoriesView
        permission={permission}
        categoriesToAdd={categoriesToAdd}
        reformattedCategories={reformattedCategories}
        onAdd={handleAdd}
        onDelete={handleDelete}
        onLabel={handleLabel}
        onScore={handleScore}
        isAdmin={role === ADMIN}
      />
    </>
  );
};

export default SiteCategoriesContainer;
